import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Descriptions, Card, Typography, Tag, Modal, Button, Collapse } from 'antd';
import { Handle, Position } from '@xyflow/react';
import classNames from 'classnames';
import { nodeColorMap } from './CustomNode';
import styles from '../RunDetailView.module.scss';
const LoopNode = ({ data }) => {
    const { step, isFirst, isLast, onViewIteration } = data;
    const { t } = useTranslation();
    const color = nodeColorMap.loop;
    const [jsonModalVisible, setJsonModalVisible] = useState(false);
    const [jsonModalContent, setJsonModalContent] = useState('');
    const [jsonModalTitle, setJsonModalTitle] = useState('');
    const showJsonModal = (title, content) => {
        setJsonModalTitle(title);
        setJsonModalContent(JSON.stringify(content, null, 2));
        setJsonModalVisible(true);
    };
    const renderCollapsibleJson = (json, label) => {
        const jsonString = JSON.stringify(json, null, 2);
        const isLong = jsonString.length > 160;
        return (React.createElement("div", null,
            React.createElement("pre", { className: classNames(styles.collapsibleJson, { [styles.collapsibleJsonLong]: isLong }) }, jsonString),
            React.createElement("div", { className: styles.viewMoreContainer },
                React.createElement(Button, { type: "text", size: "small", onClick: () => showJsonModal(label, json) },
                    React.createElement(Trans, { i18nKey: "runViewMore" })))));
    };
    const renderIterationSteps = (steps) => {
        if (!steps || steps.length === 0) {
            return React.createElement(Typography.Text, { type: "secondary" },
                React.createElement(Trans, { i18nKey: "runNoSteps" }));
        }
        return (React.createElement("div", { style: { display: 'flex', flexWrap: 'wrap', gap: 4 } }, steps.map((s, i) => {
            var _a, _b;
            if (s.type === 'loop') {
                return (React.createElement(Tag, { key: i, color: nodeColorMap.loop },
                    React.createElement(Trans, { i18nKey: "runLoopTag", values: { position: s.position, count: (_b = (_a = s.iterations) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0 }, defaults: "Loop ({{position}}) - {{count}} iters" })));
            }
            return (React.createElement(Tag, { key: i, color: nodeColorMap[s.type] || '#aaa' },
                s.type,
                s.name ? `: ${s.name}` : ''));
        })));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Card, { title: React.createElement("strong", { className: styles.nodeTitle }, t('loopNodeTitle', '{{loopType}} (position: {{position}}, index #{{index}})', {
                loopType: step.loopType,
                position: step.position,
                index: step.indexInPosition,
            })), size: "small", className: classNames(styles.node, styles.runNode), style: { borderColor: color, width: 450 }, extra: React.createElement(Tag, { color: color, className: styles.nodeType }, "LOOP"), bodyStyle: { padding: '12px' } },
            React.createElement("div", { className: "nodrag", onMouseDown: (e) => e.stopPropagation() },
                React.createElement(Descriptions, { bordered: true, size: "small", column: 1, style: { marginBottom: 12 } },
                    React.createElement(Descriptions.Item, { label: React.createElement(Trans, { i18nKey: "durationLabel", defaults: "Duration" }) },
                        step.executionTime,
                        " ms"),
                    React.createElement(Descriptions.Item, { label: React.createElement(Trans, { i18nKey: "inputComputedLabel", defaults: "Input Computed" }) }, renderCollapsibleJson(step.inputComputed, t('loopInputIterable'))),
                    React.createElement(Descriptions.Item, { label: React.createElement(Trans, { i18nKey: "iterationsLabel", defaults: "Iterations" }) },
                        React.createElement(Trans, { i18nKey: "runLoopIterations", values: { count: step.iterations.length } }))),
                React.createElement(Collapse, { defaultActiveKey: [] }, step.iterations.map((iter, index) => (React.createElement(Collapse.Panel, { key: String(index), header: React.createElement("span", null,
                        React.createElement(Trans, { i18nKey: "runIterationHeader", values: { index: index + 1 } }),
                        " \u2014 ",
                        iter.success ? (React.createElement(Tag, { color: "green", style: { margin: 0 } },
                            React.createElement(Trans, { i18nKey: "success", defaults: "Success" }))) : (React.createElement(Tag, { color: "red", style: { margin: 0 } },
                            React.createElement(Trans, { i18nKey: "failed", defaults: "Failed" }))),
                        " \u2014 ",
                        iter.executionTime,
                        "ms") },
                    React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
                        iter.conditionsResult !== undefined && (React.createElement("div", null,
                            React.createElement(Typography.Text, { strong: true },
                                React.createElement(Trans, { i18nKey: "conditionsLabel", defaults: "Conditions:" }),
                                " "),
                            React.createElement(Tag, { color: iter.conditionsResult ? 'green' : 'red' }, String(iter.conditionsResult)))),
                        React.createElement("div", null,
                            React.createElement(Typography.Text, { strong: true },
                                React.createElement(Trans, { i18nKey: "stepsLabel", defaults: "Steps:" }),
                                " "),
                            renderIterationSteps(iter.steps)),
                        React.createElement("div", { className: styles.viewMoreContainer, style: { justifyContent: 'flex-start' } },
                            React.createElement(Button, { type: "default", size: "small", onClick: () => onViewIteration(iter, index) },
                                React.createElement(Trans, { i18nKey: "runViewIterationDetails" }))))))))),
            !isFirst && React.createElement(Handle, { type: "target", position: Position.Left, id: "target" }),
            !isLast && React.createElement(Handle, { type: "source", position: Position.Right, id: "source" })),
        React.createElement(Modal, { title: jsonModalTitle, open: jsonModalVisible, onOk: () => setJsonModalVisible(false), onCancel: () => setJsonModalVisible(false), width: 800, footer: [
                React.createElement(Button, { key: "close", onClick: () => setJsonModalVisible(false) },
                    React.createElement(Trans, { i18nKey: "close", defaults: "Close" })),
            ] },
            React.createElement("pre", { style: { whiteSpace: 'pre-wrap', wordBreak: 'break-all' } }, jsonModalContent))));
};
export default LoopNode;
